import React from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import StackedTopEntityChart from 'src/components/BeaconV2Components/TopEntityChartV2/StackedTopEntityChart';
import { useAppSelector, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { useStacklineTheme } from '@stackline/ui';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import LegendWithMultipleElement from 'src/components/BeaconV2Components/Legend/LegendWithMultipleElement';
import { useMetricDataByWeekId } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useMetricDataByWeekId';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import saveAs from 'file-saver';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

export interface StackedChartDataSet {
  name: string;
  data: { y: number; color: string }[];
  color: string;
}

interface ReviewVolumeByStarData {
  weekId: string;
  weeklyOneStarReviewVolume_sum_value: number;
  weeklyTwoStarsReviewVolume_sum_value: number;
  weeklyThreeStarsReviewVolume_sum_value: number;
  weeklyFiveStarsReviewVolume_sum_value: number;
  weeklyFourStarsReviewVolume_sum_value: number;
}

export const useWeeklyReviewVolumeByStarData = () => {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);

  const { data: childWeeklyRatingVolumeByStar, isLoading: childWeeklyRatingVolumeByStarLoading } = useMetricDataByWeekId({
    startWeekId: startWeek,
    endWeekId: endWeek,
    requestId: 'review-weeklyReviewVolumeByStar',
    fields: [
      { name: 'weeklyOneStarReviewVolume' },
      { name: 'weeklyTwoStarsReviewVolume' },
      { name: 'weeklyThreeStarsReviewVolume' },
      { name: 'weeklyFourStarsReviewVolume' },
      { name: 'weeklyFiveStarsReviewVolume' }
    ],
    indexName: BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2
  });

  return {
    data: (childWeeklyRatingVolumeByStar as ReviewVolumeByStarData[]).sort((a, b) => Number(a.weekId) - Number(b.weekId)),
    isLoading: childWeeklyRatingVolumeByStarLoading
  };
};

/**
 * Weekly Review volume - that can be found on review tab - the stacked top entity chart
 */
const WeeklyReviewVolumeByStarRatingChart = () => {
  const theme = useStacklineTheme();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  const { data: weeklyReviewVolumeByStar, isLoading: weeklyReviewVolumeByStarLoading } =
    useWeeklyReviewVolumeByStarData();

  const categoryData: string[] = [];

  const stackedChartDataset: StackedChartDataSet[] = [
    { name: '1 Star', data: [], color: theme.colors.accentMagenta },
    { name: '2 Stars', data: [], color: theme.colors.accentTangerine },
    { name: '3 Stars', data: [], color: theme.colors.accentGold },
    { name: '4 Stars', data: [], color: theme.colors.success },
    { name: '5 Stars', data: [], color: theme.colors.accentPine }
  ];

  weeklyReviewVolumeByStar.forEach((datum) => {
    const {
      weekId,
      weeklyOneStarReviewVolume_sum_value = 0,
      weeklyTwoStarsReviewVolume_sum_value = 0,
      weeklyThreeStarsReviewVolume_sum_value = 0,
      weeklyFourStarsReviewVolume_sum_value = 0,
      weeklyFiveStarsReviewVolume_sum_value = 0
    } = datum;

    if (weekId !== undefined) {
      categoryData.push(String(weekId));

      const dataItems = [
        weeklyOneStarReviewVolume_sum_value,
        weeklyTwoStarsReviewVolume_sum_value,
        weeklyThreeStarsReviewVolume_sum_value,
        weeklyFourStarsReviewVolume_sum_value,
        weeklyFiveStarsReviewVolume_sum_value
      ];

      // if it is the latest week, we will push it as separate series, so it will always stay on the right side
      dataItems.forEach((value, dataItemIndex) => {
        stackedChartDataset[dataItemIndex].data.push({
          y: Math.round(value),
          color: stackedChartDataset[dataItemIndex].color
        });
      });
    }
  });

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const fileTitle = 'csv_data';
        const headers = ['Week ID', '1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'];

        try {
          const oneStarData = stackedChartDataset.find((data) => data.name === '1 Star')?.data;
          const twoStarsData = stackedChartDataset.find((data) => data.name === '2 Stars')?.data;
          const threeStarsData = stackedChartDataset.find((data) => data.name === '3 Stars')?.data;
          const fourStarsData = stackedChartDataset.find((data) => data.name === '4 Stars')?.data;
          const fiveStarsData = stackedChartDataset.find((data) => data.name === '5 Stars')?.data;

          const csvData = oneStarData.map((_, index) => {
            const weekId = categoryData[index];
            const oneStarDatum = oneStarData[index];
            const twoStarsDatum = twoStarsData[index];
            const threeStarsDatum = threeStarsData[index];
            const fourStarsDatum = fourStarsData[index];
            const fiveStarsDatum = fiveStarsData[index];

            return [weekId, oneStarDatum.y, twoStarsDatum.y, threeStarsDatum.y, fourStarsDatum.y, fiveStarsDatum.y];
          });
          const csvDataString = [headers, ...csvData].map((row) => row.join(',')).join('\n');

          const blob = new Blob([csvDataString], { type: 'text/csv;charset=utf-8' });
          saveAs(blob, `${fileTitle}.csv`);
        } catch (error) {
          console.error('Failed to save chart as delimited data in WeeklyReviewVolumeByStarRatingChart', error);
        }
      }}
      loading={weeklyReviewVolumeByStarLoading}
      loadingComponent={<SplineChartLoading width={900} />}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the review volume for ${entityPhrase} `}
            {taxonomyText}{' '}
            {`during the
            ${mainTimePeriodShortDisplayName} period.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      subtitleSX={{ marginTop: '8px' }}
      includeSummaryInfo={false}
      customLegends={
        <LegendWithMultipleElement
          multipleLegendsProps={[
            { displayName: '5 Stars', barColor: 'accentPine' },
            { displayName: '4 Stars', barColor: 'success' },
            { displayName: '3 Stars', barColor: 'accentGold' },
            { displayName: '2 Stars', barColor: 'accentTangerine' },
            { displayName: '1 Star', barColor: 'accentMagenta' }
          ]}
        />
      }
      title="Weekly Review Volume by Star Rating"
    >
      <StackedTopEntityChart
        maxNumberOfColumns={8}
        reverseAxisStartPosition
        chartSeries={[
          {
            type: 'column',
            data: stackedChartDataset,
            metricType: METRICTYPE.VOLUME,
            groupBy: 'weekId'
          }
        ]}
        tooltipCategoryTitle="REVIEW VOLUME"
        categories={categoryData}
        chartOptionsOverride={(option) => {
          const copiedOption = { ...option };
          copiedOption.chart.marginLeft = 40;
          copiedOption.chart.width = 1012;
          copiedOption.yAxis.labels = {
            style: {
              color: 'transparent'
            }
          };

          return option;
        }}
      />
    </BeaconChartWithLegend>
  );
};

export default WeeklyReviewVolumeByStarRatingChart;
